import type { ReactNode } from 'react';
import { CheckCircle2, ChevronDown, FileInput, Info } from 'lucide-react';
import type { BookingDocument } from '../types';
import { hasBookingMetadata } from '../utils/bookingStatus';
import { BookingDocuments } from './BookingDocuments';
import { shortDateLabel } from '../utils/tripUtils';
import { formatClock } from '../utils/schedulePrefs';

/** Native disclosure keeps every saved-item Details heading and keyboard behaviour consistent. */
export function ItemDetailsSection({ children, label = 'Details', id }: { children: ReactNode; label?: string; id?: string }) {
  return <details id={id} className="group/item-details">
    <summary className="flex items-center gap-2 min-h-11 cursor-pointer list-none text-gold [&::-webkit-details-marker]:hidden">
      <Info className="w-3 h-3" aria-hidden="true" />
      <span className="text-[11px] font-black uppercase tracking-widest leading-none">{label}</span>
      <ChevronDown className="w-3.5 h-3.5 ml-auto transition-transform group-open/item-details:rotate-180" aria-hidden="true" />
    </summary>
    <div className="pt-2 space-y-4">{children}</div>
  </details>;
}

export function ImportTag() {
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-tight uppercase text-gold shrink-0"><FileInput className="w-3 h-3" aria-hidden="true" />Import</span>;
}

export function BookingBanner({ targetId, label = 'Booked' }: { targetId: string; label?: string }) {
  return <button type="button" aria-controls={targetId} aria-label="View booking information"
    onClick={() => {
      const section = document.getElementById(targetId) as HTMLDetailsElement | null;
      if (section) { section.open = true; section.scrollIntoView({ block: 'nearest' }); section.querySelector('summary')?.focus(); }
    }} className="shrink-0 bg-sky-50 border-b border-sky-100 flex items-center gap-2 px-6 py-3 min-h-11 text-sky-800 text-sm hover:bg-sky-100">
      <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" /> {label}
      {/* No "Booking & payment" label here: the section it opens already says it. */}
      <ChevronDown className="ml-auto w-4 h-4" aria-hidden="true" />
  </button>;
}

export function SavedBookingInformation({ item }: { item: {
  booked?: boolean; ref?: string; confirmRef?: string; bookingService?: string;
  bookingNotes?: string; documents?: BookingDocument[]; bookingDate?: string; time?: string;
} }) {
  if (!hasBookingMetadata(item)) return null;
  return <section className="border-t border-app-border/30 pt-4 space-y-3">
    <h3 className="text-sm font-medium text-app-sub">Booking</h3>
    {item.booked && <p className="text-sm text-app-sub">Booking confirmed</p>}
    {(item.bookingDate || item.time) && <p className="text-sm text-app-sub">{[item.bookingDate && shortDateLabel(item.bookingDate), item.time && formatClock(item.time)].filter(Boolean).join(' · ')}</p>}
    {item.bookingService?.trim() && <p className="text-sm text-app-text"><span className="text-app-sub">Booked through:</span> {item.bookingService}</p>}
    {(item.ref?.trim() || item.confirmRef?.trim()) && <p className="text-sm text-app-text break-words"><span className="text-app-sub">Confirmation reference:</span> {item.ref || item.confirmRef}</p>}
    <BookingDocuments docs={item.documents} notes={item.bookingNotes} />
  </section>;
}
